import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useContext,
    useEffect,
    useState
} from 'react'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

export const useLenis = (): Lenis | null => useContext(LenisContext)

// Smooth, inertia based scrolling. Skipped entirely for visitors who prefer reduced motion.
export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const instance = new Lenis({
            autoRaf: true,
            anchors: true,
            lerp: 0.11,
            smoothWheel: true
        })

        setLenis(instance)

        return () => {
            instance.destroy()
            setLenis(null)
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}

// Scroll to an in-page target, smoothly when available and instantly otherwise.
export const scrollToTarget = (lenis: Lenis | null, target: string | number): void => {
    if (lenis) {
        lenis.scrollTo(target, { duration: 1.4 })
        return
    }

    if (typeof target === 'number') {
        window.scrollTo({ top: target })
        return
    }

    document.querySelector(target)?.scrollIntoView()
}
